import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, MessageSquare, RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = window.location.origin + '/api';

/**
 * Account Deactivation Popup
 * Shows when a user's account has been deactivated
 * Cannot be closed - forced display on all pages
 * User can submit dispute and see admin notes/updates
 */
export default function AccountDeactivatedPopup({ 
  reason, 
  deactivatedAt,
  userEmail,
  userName,
  userId
}) {
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketMessage, setTicketMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [isLoadingDisputes, setIsLoadingDisputes] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Check if account has been reactivated
  const checkAccountStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }
      
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.is_active) {
        toast.success('Your account has been reactivated! Redirecting...');
        // Reload to clear the popup and restore normal access
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.info('Your account is still suspended. Please wait for admin review.');
      }
    } catch (error) {
      toast.error('Unable to check status. Please try again.');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Fetch existing disputes for this user related to account suspension
  const fetchDisputes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/disputes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter disputes related to account suspension
      const accountDisputes = response.data.filter(d => 
        d.category === 'account_suspension' || 
        d.reason?.toLowerCase().includes('deactivat') ||
        d.reason?.toLowerCase().includes('suspend') ||
        d.reason?.toLowerCase().includes('account')
      );
      
      setDisputes(accountDisputes);
      
      // Auto-select the most recent one if exists
      if (accountDisputes.length > 0) {
        setSelectedDispute(accountDisputes[0]);
      }
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setIsLoadingDisputes(false);
    }
  }, []);

  useEffect(() => {
    fetchDisputes();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDisputes, 30000);
    return () => clearInterval(interval);
  }, [fetchDisputes]);

  const handleSubmitDispute = async () => {
    if (!ticketMessage.trim()) {
      toast.error('Please provide details about your appeal');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Submit as a dispute
      const response = await axios.post(`${API}/disputes`, {
        category: 'account_suspension',
        reason: 'Account Suspension Appeal',
        description: `Appeal Message:\n${ticketMessage}\n\n---\nOriginal Deactivation Reason: ${reason}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Appeal submitted successfully. An admin will review your case.');
      setShowTicketForm(false);
      setTicketMessage('');
      
      // Refresh disputes list
      await fetchDisputes();
    } catch (error) {
      console.error('Dispute submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit appeal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-PH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'rejected':
      case 'closed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending Review';
      case 'in_progress': return 'Under Review';
      case 'resolved': return 'Resolved';
      case 'rejected': return 'Rejected';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}} modal={true}>
      <DialogContent 
        className="sm:max-w-lg max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose={true}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            Account Suspended
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Deactivation Reason */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm font-medium text-red-800 mb-2">
              Reason for Suspension:
            </p>
            <p className="text-sm text-red-700 break-words whitespace-normal">
              {reason || 'Your account has been suspended by an administrator.'}
            </p>
            {deactivatedAt && (
              <p className="text-xs text-red-500 mt-2">
                Suspended on: {formatDate(deactivatedAt)}
              </p>
            )}
          </div>

          {/* Existing Disputes / Appeals */}
          {!showTicketForm && (
            <>
              {isLoadingDisputes ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                  <span className="ml-2 text-sm text-slate-500">Loading appeals...</span>
                </div>
              ) : disputes.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">Your Appeals</p>
                    <button 
                      onClick={fetchDisputes}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                  </div>
                  
                  {disputes.map((dispute) => (
                    <div 
                      key={dispute.id}
                      className={`p-3 border rounded-xl cursor-pointer transition-colors ${
                        selectedDispute?.id === dispute.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedDispute(dispute)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono text-slate-500">
                          #{dispute.id.slice(0, 8).toUpperCase()}
                        </span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(dispute.status)}
                          <span className="text-xs font-medium">
                            {getStatusLabel(dispute.status)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600">
                        Submitted: {formatDate(dispute.created_at)}
                      </p>
                    </div>
                  ))}

                  {/* Selected Dispute Details with Admin Notes */}
                  {selectedDispute && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Your Appeal:</p>
                        <p className="text-sm text-slate-700 break-words whitespace-pre-wrap">
                          {selectedDispute.description}
                        </p>
                      </div>
                      
                      {/* Show public response from admin (resolution field) - NOT admin_notes which is internal */}
                      {selectedDispute.resolution && (
                        <div className="pt-3 border-t border-slate-200">
                          <p className="text-xs font-medium text-blue-600 mb-1">Admin Response:</p>
                          <p className="text-sm text-slate-700 break-words whitespace-pre-wrap bg-white p-3 rounded-lg border border-blue-100">
                            {selectedDispute.resolution}
                          </p>
                        </div>
                      )}
                      
                      {selectedDispute.resolved_at && (
                        <p className="text-xs text-slate-500">
                          Resolved on: {formatDate(selectedDispute.resolved_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <p className="text-sm text-slate-700">
                    You haven't submitted an appeal yet. If you believe this suspension was a mistake, 
                    you can submit an appeal below. Our team will review your case and respond.
                  </p>
                </div>
              )}

              <Button
                onClick={() => setShowTicketForm(true)}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                data-testid="submit-appeal-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {disputes.length > 0 ? 'Submit Another Appeal' : 'Submit Appeal'}
              </Button>

              {/* Check Account Status Button */}
              <Button
                onClick={checkAccountStatus}
                variant="outline"
                className="w-full h-10 border-green-500 text-green-700 hover:bg-green-50"
                disabled={isCheckingStatus}
                data-testid="check-status-btn"
              >
                {isCheckingStatus ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Check if Account is Reactivated
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-slate-500">
                Account status is checked automatically every 10 seconds
              </p>
            </>
          )}

          {/* Appeal Form */}
          {showTicketForm && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-700">
                  <strong>Category:</strong> Account Suspension Appeal<br />
                  <strong>Note:</strong> An admin will review your appeal and respond. You can check back here for updates.
                </p>
              </div>

              <div>
                <Label>Your Appeal Message *</Label>
                <Textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please explain why you believe your account should be reactivated. Provide any relevant information that may help us review your case..."
                  className="mt-1.5 h-32 resize-none"
                  data-testid="appeal-message-input"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowTicketForm(false)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={handleSubmitDispute}
                  disabled={isSubmitting || !ticketMessage.trim()}
                  data-testid="submit-appeal-confirm-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Appeal'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
